import { useState } from 'react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { bigOf, type Metric } from '../engine';

export interface CostCell {
  label: string;
  metric: Metric;
  /** Sustituye la nota automática («falta …» / «medido»). */
  note?: string;
}

/**
 * El bloque de coste. Va cerrado por defecto: es lo que el cliente compró
 * aparte, y se revela con un clic. Cada celda dice su cifra o lo que le falta
 * para tenerla; un coste que no se puede computar nunca baja a cero.
 */
export function CostCells({ cells, defaultOpen = false, className }: { cells: CostCell[]; defaultOpen?: boolean; className?: string }) {
  const t = useT();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={cn('flex flex-col gap-4 rounded-[14px] border p-[22px]', className)} style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-[22px] font-bold tracking-[-0.01em]" style={{ color: 'var(--color-dash-fg)' }}>{t('dash.cost.title')}</h3>
          <span className="dash-mono rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em]" style={{ background: 'var(--color-dash-orange-soft)', color: 'var(--color-dash-orange)' }}>{t('dash.cost.offByDefault')}</span>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-sm font-semibold hover:underline" style={{ color: 'var(--color-dash-olive)' }}>
          {open ? t('dash.cost.hide') : t('dash.cost.reveal')}
        </button>
      </div>
      {open && (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
          {cells.map((c) => {
            const pending = c.metric.state === 'PENDING';
            const note =
              c.note ??
              (pending
                ? c.metric.needs
                  ? t('dash.needs', { what: t(`dash.need.${c.metric.needs}`, { n: c.metric.needsCount ?? 0 }) })
                  : t('dash.pending')
                : c.metric.unit === 'percent' && c.metric.base != null
                  ? t('dash.basis', { n: c.metric.base.toLocaleString(), moe: c.metric.moe ?? 0 })
                  : t('dash.measured'));
            return (
              <div key={c.label} className="flex flex-col gap-[5px]">
                <span className="dash-mono text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: 'var(--color-dash-fg-muted)' }}>{c.label}</span>
                <span className="text-[28px] font-bold leading-[1.1] tabular-nums" style={{ color: pending ? 'var(--color-dash-orange)' : 'var(--color-dash-fg)' }}>{bigOf(c.metric, t('dash.pending'))}</span>
                <span className="text-xs leading-[1.45] [text-wrap:pretty]" style={{ color: pending ? 'var(--color-dash-orange)' : 'var(--color-dash-fg-muted)' }}>{note}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
